<template>
  <div id="quick-task-prompt">
    <div class="mx-auto max-w-lg py-8">
      <QuickTaskLoader
        v-if="
          isPending ||
          isLoadingTaskOrContent ||
          newTaskSTatus.isQueued ||
          newTaskSTatus.isRunning
        "
      />
      <template v-else>
        <p class="mr-auto">{{ task?.description }}</p>
        <Message
          v-if="errorMessage"
          id="quick-task-error"
          severity="error"
          closable
          @close="errorMessage = ''"
        >
          {{ errorMessage }}
        </Message>
        <div data-flip-key="task-main">
          <ContentFormMediaSupport
            :category="category"
            :loading="isPending"
            is-quick-task
            @submit="createTask"
          />
        </div>
        <AppButton
          id="quick-task-cancel-prompt"
          variant="secondary"
          text
          :label="$t('buttons.cancel')"
          class="w-full"
          :data-gtm-action="analyticsEvents.QUICK_TASK.quick_task_cancel_prompt"
          @click="cancel"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import Message from "@/volt/Message.vue";
import QuickTaskLoader from "@/components/quickTask/meta/QuickTaskLoader.vue";
import ContentFormMediaSupport from "@/components/forms/ContentFormMediaSupport.vue";

const props = defineProps<{
  beforeChange?: () => void;
  afterChange?: () => void;
}>();

const emit = defineEmits<{
  addRecentTask: [string];
}>();

const { t } = useI18n();
import type { ContentInput } from "@/types/formData";
import { useI18n } from "vue3-i18n";
const { currentTaskKey, newTaskId, isLoadingTaskOrContent, newTaskSTatus, taskResult } =
  useQuickTask();
const { getTaskByName } = useTaskConfigDataCore();
const { isPending, mutate } = createNewSingleTask();
const errorMessage = ref("");

async function createTask(payload: ContentInput) {
  if (isPending.value || !currentTaskKey.value) return;
  if (props.beforeChange) props.beforeChange();
  mutate(
    {
      taskType: currentTaskKey.value!,
      formData: payload,
    },
    {
      onSuccess(data) {
        newTaskId.value = data.id;
      },
      onError(e) {
        const message = e.message || t("errors.tryAgain");
        errorMessage.value = message;
      },
    }
  );
  emit("addRecentTask", currentTaskKey.value);
  if (props.afterChange) props.afterChange();
}

function cancel() {
  if (props.beforeChange) props.beforeChange();
  currentTaskKey.value = "";
  if (props.afterChange) props.afterChange();
}

const category = computed(() => {
  return currentTaskKey.value?.split("/")[0] || "";
});

const task = computed(() => {
  return getTaskByName(currentTaskKey.value);
});

onBeforeUnmount(() => {
  if (props.beforeChange) props.beforeChange();
});

watchEffect(() => {
  if (taskResult.value?.error === 1) {
    errorMessage.value = taskResult.value.failedMessage || t("errors.tryAgain");
    newTaskId.value = "";
  }
});
</script>
